/**
 * Secure token storage utilities
 *
 * Strategy:
 * - Access tokens in memory (cleared on tab close) for XSS protection
 * - Refresh tokens in sessionStorage by default (cleared on tab close),
 *   or localStorage when the user opts into "Remember me" (persists across browser restarts)
 * - No localStorage unless the user explicitly opts in
 */

interface TokenData {
  accessToken: string;
  refreshToken: string | null;
  expiresAt: number; // Unix timestamp in milliseconds
}

class TokenStorage {
  private tokens: TokenData | null = null;
  private readonly REFRESH_TOKEN_KEY = 'smartfield_refresh_token';
  private readonly ACCESS_TOKEN_KEY = 'smartfield_access_token';
  private readonly EXPIRES_AT_KEY = 'smartfield_expires_at';
  private rememberMe = false;

  private get persistentStore(): Storage {
    return this.rememberMe ? localStorage : sessionStorage;
  }

  /**
   * Initialize token storage from sessionStorage/localStorage on app start
   */
  initialize(): TokenData | null {
    const store = localStorage.getItem(this.REFRESH_TOKEN_KEY) ? localStorage : sessionStorage;
    this.rememberMe = store === localStorage;

    const refreshToken = store.getItem(this.REFRESH_TOKEN_KEY);
    const accessToken = store.getItem(this.ACCESS_TOKEN_KEY);
    const expiresAt = store.getItem(this.EXPIRES_AT_KEY);

    if (accessToken && expiresAt) {
      this.tokens = {
        accessToken,
        refreshToken,
        expiresAt: parseInt(expiresAt, 10),
      };

      // Clear the access token after reading to keep it in memory only
      store.removeItem(this.ACCESS_TOKEN_KEY);
      store.removeItem(this.EXPIRES_AT_KEY);

      return this.tokens;
    }

    return null;
  }

  /**
   * Store tokens in memory and persist refresh token
   */
  setTokens(accessToken: string, refreshToken: string | null, expiresInSeconds: number, rememberMe = this.rememberMe): void {
    this.rememberMe = rememberMe;
    const expiresAt = Date.now() + expiresInSeconds * 1000;

    this.tokens = {
      accessToken,
      refreshToken,
      expiresAt,
    };

    // Refresh token always goes to the store matching rememberMe; clear the other store to avoid stale copies
    (rememberMe ? sessionStorage : localStorage).removeItem(this.REFRESH_TOKEN_KEY);
    if (refreshToken) {
      this.persistentStore.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    }
  }

  /**
   * Get current access token if not expired
   */
  getAccessToken(): string | null {
    if (!this.tokens) return null;

    // Check if token is expired
    if (Date.now() >= this.tokens.expiresAt) {
      return null;
    }

    return this.tokens.accessToken;
  }

  /**
   * Get refresh token
   */
  getRefreshToken(): string | null {
    return this.tokens?.refreshToken || this.persistentStore.getItem(this.REFRESH_TOKEN_KEY);
  }

  /**
   * Check if access token will expire soon (within 5 minutes)
   */
  shouldRefresh(): boolean {
    if (!this.tokens) return false;

    const fiveMinutesFromNow = Date.now() + 5 * 60 * 1000;
    return this.tokens.expiresAt <= fiveMinutesFromNow;
  }

  /**
   * Get time until token expiration in milliseconds
   */
  getTimeUntilExpiry(): number | null {
    if (!this.tokens) return null;
    return Math.max(0, this.tokens.expiresAt - Date.now());
  }

  /**
   * Clear all tokens
   */
  clear(): void {
    this.tokens = null;
    sessionStorage.removeItem(this.REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(this.ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(this.EXPIRES_AT_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.EXPIRES_AT_KEY);
    this.rememberMe = false;
  }

  /**
   * Persist current tokens to sessionStorage/localStorage (for page refresh recovery)
   */
  persistToSession(): void {
    if (this.tokens) {
      this.persistentStore.setItem(this.ACCESS_TOKEN_KEY, this.tokens.accessToken);
      this.persistentStore.setItem(this.EXPIRES_AT_KEY, this.tokens.expiresAt.toString());
      if (this.tokens.refreshToken) {
        this.persistentStore.setItem(this.REFRESH_TOKEN_KEY, this.tokens.refreshToken);
      }
    }
  }
}

export const tokenStorage = new TokenStorage();
